import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useEffect, useRef, useState } from 'react';
const API = apiUrl("");
const ROOT = '/api/headoffice/handover-tasks';
async function post(path, body = {}) {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('jwt') || '';
  const res = await fetch(API + path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
const input = {
  padding: 8,
  border: '1px solid #D9D9D9',
  borderRadius: 5,
  font: 'inherit',
  boxSizing: 'border-box',
  width: '100%'
};
const button = {
  padding: '8px 14px',
  border: '1px solid #D9D9D9',
  borderRadius: 6,
  cursor: 'pointer',
  background: '#fff',
  color: '#434343',
  fontFamily: 'inherit',
  fontWeight: 600
};
const cell = {
  padding: '12px 10px',
  textAlign: 'left',
  borderBottom: '1px solid #F0F0F0'
};
const date = value => value ? new Date(value).toLocaleDateString('en-GB', {
  timeZone: 'UTC'
}) : '—';
export default function HandoverTasks() {
  const [pharmacies, setPharmacies] = useState([]),
    [dbName, setDbName] = useState('');
  const [staff, setStaff] = useState([]),
    [tasks, setTasks] = useState([]),
    [selected, setSelected] = useState([]);
  const [topic, setTopic] = useState(''),
    [description, setDescription] = useState(''),
    [dueDate, setDueDate] = useState('');
  const [file, setFile] = useState(null),
    [creating, setCreating] = useState(false),
    [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [detailId, setDetailId] = useState(null),
    [search, setSearch] = useState('');
  const generation = useRef(0),
    submitLock = useRef(false);
  useEffect(() => {
    let live = true;
    post('/api/headoffice/staff-messages/staff').then(d => {
      if (!live) return;
      setPharmacies(d.pharmacies || []);
      setDbName(d.pharmacies?.[0]?.dbName || '');
    }).catch(e => {
      if (live) setError(e.message);
    });
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    const version = ++generation.current;
    setTasks([]);
    setStaff([]);
    setSelected([]);
    setDetailId(null);
    setCreating(false);
    setError('');
    setNotice('');
    if (!dbName) return;
    setLoading(true);
    Promise.all([post(ROOT + '/staff', {
      dbName
    }), post(ROOT + '/list', {
      dbName
    })]).then(([s, t]) => {
      if (version !== generation.current) return;
      setStaff(s.staff);
      setTasks(t.tasks);
    }).catch(e => {
      if (version === generation.current) setError(e.message);
    }).finally(() => {
      if (version === generation.current) setLoading(false);
    });
    return () => {
      generation.current++;
    };
  }, [dbName]);
  async function refresh() {
    const version = generation.current;
    setLoading(true);
    setError('');
    try {
      const d = await post(ROOT + '/list', {
        dbName
      });
      if (version === generation.current) setTasks(d.tasks);
    } catch (e) {
      if (version === generation.current) setError(e.message);
    } finally {
      if (version === generation.current) setLoading(false);
    }
  }
  async function save(e) {
    e.preventDefault();
    if (submitLock.current) return;
    submitLock.current = true;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      let attachment = null;
      if (file) {
        if (!file.size || file.size > 8 * 1024 * 1024) throw new Error('Choose a non-empty file up to 8 MB');
        const dataBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1]);
          reader.onerror = () => reject(new Error('Unable to read file'));
          reader.readAsDataURL(file);
        });
        attachment = {
          filename: file.name,
          dataBase64
        };
      }
      const d = await post(ROOT + '/create', {
        dbName,
        topic,
        description,
        dueDate: dueDate || null,
        staffIds: selected,
        attachment
      });
      setCreating(false);
      setTopic('');
      setDescription('');
      setDueDate('');
      setSelected([]);
      setFile(null);
      setNotice(`Task created for ${d.recipientCount} recipient${d.recipientCount === 1 ? '' : 's'}.`);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      submitLock.current = false;
      setSaving(false);
    }
  }
  const detail = tasks.find(t => t.id === detailId);
  const visibleStaff = staff.filter(s => `${s.name} ${s.email}`.toLowerCase().includes(search.toLowerCase()));
  return <div style={{
    padding: 16,
    background: '#fff',
    border: '1px solid #F0F0F0',
    borderRadius: 8
  }}>
    <div style={{
      display: 'flex',
      gap: 10,
      flexWrap: 'wrap',
      alignItems: 'center'
    }}>
      <label>Pharmacy <select aria-label="Pharmacy" style={{
          ...input,
          width: 'auto'
        }} value={dbName} disabled={saving} onChange={e => setDbName(e.target.value)}>
        {!pharmacies.length && <option value="">No pharmacies available</option>}
        {pharmacies.map(p => <option key={p.dbName} value={p.dbName}>{p.pharmacyName}</option>)}
      </select></label>
      <button style={button} disabled={!dbName || loading || saving} onClick={() => {
        setCreating(true);
        setError('');
        setSearch('');
      }}>New handover task</button>
      <button style={button} disabled={!dbName || loading || saving} onClick={refresh}>Refresh statuses</button>
    </div>
    {error && <p role="alert" style={{
      color: '#CF1322'
    }}>{error}</p>}
    {notice && <p role="status" style={{
      color: '#0E8CFF'
    }}>{notice}</p>}
    {creating && <form onSubmit={save} style={{
      marginTop: 16,
      padding: 16,
      border: '1px solid #F0F0F0',
      borderRadius: 8
    }}>
      <fieldset disabled={saving} style={{
        border: 0,
        padding: 0,
        margin: 0
      }}>
        <legend style={{
          fontWeight: 700
        }}>New handover task</legend>
        <label style={{
          display: 'block',
          marginTop: 12
        }}>Topic<input style={input} required maxLength={250} value={topic} onChange={e => setTopic(e.target.value)} /></label>
        <label style={{
          display: 'block',
          marginTop: 12
        }}>Description<textarea style={input} rows={4} required maxLength={20000} value={description} onChange={e => setDescription(e.target.value)} /></label>
        <label style={{
          display: 'block',
          marginTop: 12
        }}>Due by (optional)<input style={input} type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} /></label>
        <label style={{
          display: 'block',
          marginTop: 12
        }}>Attachment (optional, maximum 8 MB)<input style={input} type="file" onChange={e => setFile(e.target.files[0] || null)} /></label>
        <p>Recipients ({selected.length} selected)</p>
        <input aria-label="Search staff" style={input} placeholder="Search staff by name or email" value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{
          margin: '8px 0',
          display: 'flex',
          gap: 8
        }}>
          <button type="button" style={button} onClick={() => setSelected([...new Set([...selected, ...visibleStaff.map(s => s.staffId)])])}>Select visible staff</button>
          <button type="button" style={button} onClick={() => setSelected([])}>Clear selection</button>
        </div>
        <div style={{
          maxHeight: 220,
          overflowY: 'auto'
        }}>
          {visibleStaff.map(s => <label key={s.staffId} style={{
            display: 'block',
            padding: 7
          }}>
            <input type="checkbox" checked={selected.includes(s.staffId)} onChange={e => setSelected(prev => e.target.checked ? [...prev, s.staffId] : prev.filter(id => id !== s.staffId))} /> {s.name} <span style={{
              color: '#5D5D5D'
            }}>{s.email}</span>
          </label>)}
          {!visibleStaff.length && <p>No active staff found.</p>}
        </div>
        <div style={{
          display: 'flex',
          gap: 8,
          marginTop: 16
        }}>
          <button style={{
            ...button,
            background: '#0E8CFF',
            color: '#fff'
          }} disabled={!selected.length || !topic.trim() || !description.trim()} type="submit">{saving ? 'Saving…' : 'Create handover task'}</button>
          <button style={button} type="button" onClick={() => setCreating(false)}>Cancel</button>
        </div>
      </fieldset>
    </form>}
    {loading ? <p role="status">Loading handover tasks…</p> : <div style={{
      overflowX: 'auto',
      marginTop: 16
    }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse'
      }}>
        <thead><tr>{['Topic', 'Recipients', 'Acknowledged', 'Due by'].map(h => <th style={cell} key={h}>{h}</th>)}</tr></thead>
        <tbody>{tasks.map(t => <tr key={t.id}>
          <td style={cell}><button style={{
                ...button,
                color: '#0E8CFF'
              }} onClick={() => setDetailId(t.id)}>{t.topic}</button></td>
          <td style={cell}>{t.recipientCount}</td><td style={cell}>{t.acknowledgedPercentage}% ({t.acknowledgedCount}/{t.recipientCount})</td><td style={cell}>{date(t.dueDate)}</td>
        </tr>)}</tbody>
      </table>
      {!tasks.length && <p>No handover tasks created through this portal for this pharmacy yet.</p>}
    </div>}
    {detail && <section aria-label="Task recipient statuses" style={{
      marginTop: 20,
      padding: 16,
      background: '#FAFAFA',
      borderRadius: 8
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12
      }}><h3 style={{
          marginTop: 0
        }}>{detail.topic}</h3><button style={button} onClick={() => setDetailId(null)}>Close details</button></div>
      <p style={{
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere'
      }}>{detail.description}</p><p>Due by: {date(detail.dueDate)}</p>
      {detail.fileUrl && <a href={detail.fileUrl} target="_blank" rel="noopener noreferrer">Download attachment</a>}
      <table style={{
        width: '100%',
        borderCollapse: 'collapse'
      }}>
        <thead><tr><th style={cell}>Name</th><th style={cell}>Status</th></tr></thead>
        <tbody>{detail.recipients.map(r => <tr key={r.staffId}><td style={cell}>{r.name}</td><td style={{
              ...cell,
              color: r.acknowledged ? '#0E8CFF' : '#AD4E00'
            }}>{r.acknowledged ? 'Acknowledged' : 'Not acknowledged'}</td></tr>)}</tbody>
      </table>
    </section>}
  </div>;
}
